import styles from './User.module.css';

import config from 'config.js';
import axios from 'axios';
import React, { useState, useEffect, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { withRouter } from 'react-router';
import {
  PageHeader,
  Card,
  Spin,
  Button,
  Select,
  Alert,
  Table,
  Input,
  List,
  Tabs,
  Tag,
  Switch,
  Descriptions,
  Statistic,
  Popconfirm,
  Typography,
  Row,
  Col,
  message,
  Modal,
  Form,
  DatePicker
} from 'antd';
import moment from 'moment';
import _uniq from 'lodash/uniq';

import MollieOrder from './MollieOrder.js';

import { ReloadOutlined, PlusOutlined } from '@ant-design/icons';

import { getUser, clearUser, deleteUser, updateUser, extendUserTrial, impersonateUser, removeLifeTimeOrder } from 'store/ducks/users.js';

const round = (v) => Math.round(v * 100) / 100;

const bulkPlanUpdateOptions = {
  ltdSolo: {
    forms: 10000,
    submissions: 1000000,
    users: 2,
    guests: 0,
    filesStorage: 2500,
    domains: 1,
    brandingRemoval: 1
  },
  ltdAgency: {
    forms: 10000,
    submissions: 1000000,
    users: 2,
    guests: 5,
    filesStorage: 5000,
    domains: 20,
    brandingRemoval: 1
  },
  ltdBusiness: {
    forms: 10000,
    submissions: 1000000,
    users: 10,
    guests: 10,
    filesStorage: 10000,
    domains: 1000,
    brandingRemoval: 1
  },
  unlimited: {
    forms: 10000,
    submissions: 1000000,
    users: 1000,
    guests: 1000,
    filesStorage: 1000,
    domains: 1000,
    brandingRemoval: 1
  },
  free: {
    forms: 1,
    submissions: 50,
    users: 1,
    guests: 0,
    filesStorage: 100,
    domains: 0,
    brandingRemoval: 0
  }
};

const User = ({ match }) => {
  const dispatch = useDispatch();

  const userId = match.params.id;
  const user = useSelector((state) => state.users.user);
  const loading = useSelector((state) => state.users.getUser.loading);
  const updating = useSelector((state) => state.users.updateUser.loading || state.users.extendUserTrial.loading);

  const [newPassword, setNewPassword] = useState('');

  const [daysToAddToTrial, setDaysToAddToTrial] = useState(0);
  const [planForms, setPlanForms] = useState(0);
  const [planSubmissions, setPlanSubmissions] = useState(0);
  const [planUsers, setPlanUsers] = useState(0);
  const [planGuests, setPlanGuests] = useState(0);
  const [planFilesStorage, setPlanFilesStorage] = useState(0);
  const [planDomains, setPlanDomains] = useState(0);
  const [planBrandingRemoval, setBrandingRemoval] = useState(0);

  const [showAddOrderModal, setShowAddOrderModal] = useState(false);

  const reload = () => dispatch(getUser(userId));
  const handleDelete = () => dispatch(deleteUser(userId)).then(() => window.history.back());
  const impersonate = () => {
    const url = process.env.NODE_ENV !== 'development' ? 'https://admin.questionscout.com' : 'http://localhost:3000';

    dispatch(impersonateUser(userId, user.owner ? user.owner._id : null)).then((id) => window.open(`${url}/impersonate/${id}`, '_blank'));
  };

  const handleChange = (name, value) => {
    dispatch(updateUser(userId, { name, value })).then(() => dispatch(getUser(userId)));
  };

  const handleExtendUserTrial = (daysToAdd) => {
    dispatch(extendUserTrial(userId, { daysToAdd })).then(() => dispatch(getUser(userId)));
  };

  const handlePlanUsageChange = (name, value) => {
    const number = parseInt(value);

    if (!Number.isInteger(number) || number > user.plan[name] || number < 0) return message.error('Invalid value');

    handleChange(`planUsage.${name}`, number);
  };

  const updatePassword = () => {
    if (newPassword.length < 6) return message.error('Password too damn short, at least 6 please');

    handleChange('password', newPassword);
    setNewPassword('');
    message.success(`Password updated successfully`);
  };

  const updatePlan = (name, value) => {
    handleChange(`plan.${name}`, value);
  };

  const bulkUpdatePlan = (name) => {
    handleChange('plan', bulkPlanUpdateOptions[name]);
  };

  useEffect(() => {
    dispatch(getUser(userId));
  }, [userId]);

  useEffect(() => {
    return () => {
      dispatch(clearUser());
    };
  }, []);

  useEffect(() => {
    if (user && user.plan) {
      setPlanForms(user.plan.forms);
      setPlanSubmissions(user.plan.submissions);
      setPlanUsers(user.plan.users);
      setPlanGuests(user.plan.guests);
      setPlanFilesStorage(user.plan.filesStorage);
      setPlanDomains(user.plan.domains);
      setBrandingRemoval(user.plan.brandingRemoval);
    }
  }, [user]);

  const removeLtdOrder = (id) => {
    dispatch(removeLifeTimeOrder(user._id, id)).then(() => dispatch(getUser(user._id)));
  };

  const splitColumns = [
    {
      title: 'paid',
      dataIndex: 'paid',
      render: (row) => (row ? 'true' : 'false')
    },
    {
      title: '_id',
      dataIndex: '_id'
    },
    {
      title: 'link',
      dataIndex: '_id',
      render: (row) => (row ? <a href={`https://admin.questionscout.com/ltdf/pay/${user._id}/${row}`}>Payment Link</a> : '-')
    },
    {
      title: 'orderId',
      dataIndex: 'orderId'
    },
    {
      title: 'prices',
      dataIndex: 'prices',
      render: (row) => (row ? `gross: $${row.gross}, net: $${row.net}, vat: $${row.vat}` : '')
    },
    {
      title: 'vatRate',
      dataIndex: 'vatRate'
    },
    {
      title: 'startsAt',
      dataIndex: 'startsAt'
    },
    {
      title: 'endsAt',
      dataIndex: 'endsAt'
    }
  ];

  const ordersColumns = [
    {
      title: '_id',
      dataIndex: '_id'
    },
    {
      title: 'Mollie id',
      dataIndex: ['order', 'id'],
      render: (id) => (
        <a href={`https://www.mollie.com/dashboard/org_3337464/orders/${id}`} target="_blank">
          {id}
        </a>
      )
    },
    {
      title: 'Amount',
      dataIndex: ['order', 'amount']
    },
    {
      title: 'Vat Amount',
      dataIndex: ['order', 'vatAmount']
    },
    {
      title: 'Vat Rate',
      dataIndex: ['order', 'vatRate']
    },
    {
      title: 'Status',
      dataIndex: ['order', 'status']
    },
    {
      title: 'Description',
      dataIndex: ['order', 'description']
    },
    {
      title: 'Created At',
      dataIndex: ['order', 'createdAt']
    },
    {
      title: 'V',
      dataIndex: ['order', 'version']
    },
    {
      title: 'Action',
      dataIndex: '',
      key: 'x',
      render: (row) => {
        const paid = ['completed', 'paid', 'shipping'].indexOf(row.order.status) !== -1;

        return (
          <>
            {!paid && (
              <Button onClick={() => removeLtdOrder(row._id)} type="link" danger>
                Remove
              </Button>
            )}
          </>
        );
      }
    }
  ];

  const createOrder = async (value) => {
    await axios({
      method: 'POST',
      url: `${config.apiUrl}/root/ltdf/order`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('user_token')}`
      },
      data: {
        user: user._id,
        order: value.order
      }
    });

    reload();
    setShowAddOrderModal(false);
  };

  console.log(user);

  return (
    <>
      <Spin spinning={loading} size="large">
        <div style={{ border: '1px solid #f0f0f0', background: '#fff', margin: '0 0 15px 0', minHeight: 80 }}>
          {user && (
            <PageHeader
              title={user.name}
              subTitle={user.email}
              onBack={() => window.history.back()}
              extra={[
                <Button key="impersonate" type="primary" ghost onClick={() => impersonate()}>
                  Impersonate
                </Button>,
                <Popconfirm
                  key="delete"
                  placement="bottomRight"
                  title={'Are you 100% sure?'}
                  onConfirm={() => handleDelete()}
                  disabled={user.authType === 'onetool'}
                  okText="Yes, delete"
                  cancelText="No">
                  <Button danger disabled={user.authType === 'onetool'}>
                    Delete
                  </Button>
                </Popconfirm>,
                <Button key="reload" onClick={() => reload()}>
                  <ReloadOutlined />
                </Button>
              ]}>
              <Descriptions size="small" column={3}>
                <Descriptions.Item label="Created At">{moment(user.createdAt).format('DD.MM.YYYY HH:mm')}</Descriptions.Item>
                <Descriptions.Item label="Updated At">{moment(user.updatedAt).format('DD.MM.YYYY HH:mm')}</Descriptions.Item>
              </Descriptions>
            </PageHeader>
          )}
        </div>
      </Spin>

      <Spin spinning={loading || updating} size="large">
        <Card style={{ width: '100%', minHeight: 224 }}>
          {user && (
            <>
              <Tabs animated={false}>
                <Tabs.TabPane tab="General" key="general">
                  <Descriptions column={3} bordered style={{ margin: '20px 0 0 0' }}>
                    <Descriptions.Item label="Deleted" span={3}>
                      {String(user.deleted)}
                    </Descriptions.Item>

                    <Descriptions.Item label="ID">{user._id}</Descriptions.Item>

                    <Descriptions.Item label="Email" span={2}>
                      {user.authType === 'local' && (
                        <Typography.Paragraph style={{ margin: 0 }} editable={{ onChange: (value) => handleChange('email', value) }}>
                          {user.email}
                        </Typography.Paragraph>
                      )}
                      {user.authType !== 'local' && user.email}
                    </Descriptions.Item>

                    <Descriptions.Item label="Last Active" span={3}>
                      {moment(user.lastActive).format('DD.MM.YYYY HH:mm')}
                    </Descriptions.Item>

                    <Descriptions.Item label="Role" span={3}>
                      <Select value={user.role} onChange={(value) => handleChange('role', value)} disabled={user.authType === 'onetool'}>
                        <Select.Option value="holder">holder</Select.Option>
                        <Select.Option value="admin">admin</Select.Option>
                        <Select.Option value="mod">mod</Select.Option>
                        <Select.Option value="user">user</Select.Option>
                      </Select>
                    </Descriptions.Item>

                    {user.owner && user.role !== 'holder' && (
                      <Descriptions.Item label="Owner" span={3}>
                        <Link to={`/users/${user.owner._id}`}>{user.owner.email}</Link>
                      </Descriptions.Item>
                    )}

                    {user.owner && user.role !== 'holder' && (
                      <Descriptions.Item label="Owner ID" span={3}>
                        {user.authType !== 'onetool' && (
                          <Typography.Paragraph style={{ margin: 0 }} editable={{ onChange: (value) => handleChange('owner', value) }}>
                            {user.owner ? user.owner._id : ''}
                          </Typography.Paragraph>
                        )}
                        {user.authType === 'onetool' && user.owner ? user.owner._id : null}
                      </Descriptions.Item>
                    )}

                    <Descriptions.Item label="Auth Type" span={3}>
                      <Select value={user.authType} onChange={(value) => handleChange('authType', value)}>
                        <Select.Option value="local">local</Select.Option>
                        <Select.Option value="google">google</Select.Option>
                        <Select.Option value="facebook">facebook</Select.Option>
                      </Select>
                    </Descriptions.Item>

                    <Descriptions.Item label="Password" span={3}>
                      <Input.Group>
                        <Row gutter={8}>
                          <Col span={5}>
                            <Input
                              placeholder={`new password`}
                              disabled={user.authType !== 'local'}
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              onPressEnter={() => updatePassword()}
                            />
                          </Col>
                          <Col span={8}>
                            <Button
                              disabled={user.authType !== 'local' || newPassword.length < 6}
                              type="primary"
                              ghost
                              onClick={() => updatePassword()}>
                              Update
                            </Button>
                          </Col>
                        </Row>
                      </Input.Group>
                    </Descriptions.Item>

                    {user.role === 'holder' && (
                      <>
                        <Descriptions.Item label="Plan Type" span={3}>
                          <Select value={user.billing.plan} onChange={(value) => handleChange('billing.plan', value)}>
                            <Select.Option value="trial">trial</Select.Option>
                            <Select.Option value="starter">starter</Select.Option>
                            <Select.Option value="personal">personal</Select.Option>
                            <Select.Option value="pro">pro</Select.Option>
                            <Select.Option value={null}>null</Select.Option>
                          </Select>
                        </Descriptions.Item>

                        <Descriptions.Item label="Trial End" span={3}>
                          {user.billing.trialEnd && (
                            <div style={{ marginBottom: 8 }}>Ends/ended at: {moment(user.billing.trialEnd).format('DD.MM.YYYY HH:mm')}</div>
                          )}

                          <Input.Group>
                            <Row gutter={8}>
                              <Col span={5}>
                                <Input
                                  type="number"
                                  disabled={['trial', null].indexOf(user.billing.plan) === -1}
                                  value={daysToAddToTrial}
                                  onChange={(e) => setDaysToAddToTrial(parseInt(e.target.value, 10))}
                                  onPressEnter={() => handleExtendUserTrial(daysToAddToTrial)}
                                />
                              </Col>
                              <Col span={8}>
                                <Button
                                  disabled={!(daysToAddToTrial > 0) || ['trial', null].indexOf(user.billing.plan) === -1}
                                  type="primary"
                                  ghost
                                  onClick={() => handleExtendUserTrial(daysToAddToTrial)}>
                                  Add days
                                </Button>
                              </Col>
                            </Row>
                          </Input.Group>
                        </Descriptions.Item>

                        <Descriptions.Item label="Plan Status" span={3}>
                          {String((user.billing && user.billing.planStatus) || null)}
                        </Descriptions.Item>
                      </>
                    )}

                    {user.role === 'holder' && user.authType !== 'onetool' && (
                      <Descriptions.Item label="Company Name" span={3}>
                        <Typography.Paragraph style={{ margin: 0 }} editable={{ onChange: (value) => handleChange('companyName', value) }}>
                          {user.companyName || ''}
                        </Typography.Paragraph>
                      </Descriptions.Item>
                    )}

                    {user.role === 'holder' && (
                      <Descriptions.Item label="Hide QS Branding">
                        <Switch checked={user.hideBranding} onChange={(value) => handleChange('hideBranding', value)} />
                      </Descriptions.Item>
                    )}

                    <Descriptions.Item label="Active">
                      <Switch
                        checked={user.active}
                        onChange={(value) => handleChange('active', value)}
                        disabled={user.authType === 'onetool'}
                      />
                    </Descriptions.Item>
                    <Descriptions.Item label="Verified">
                      <Switch checked={user.verified} onChange={(value) => handleChange('verified', value)} />
                    </Descriptions.Item>
                    <Descriptions.Item label="IP">{user.ip}</Descriptions.Item>
                    <Descriptions.Item label="Country">{user.country}</Descriptions.Item>
                  </Descriptions>

                  <Row gutter={16} style={{ margin: '40px 0 0 0' }}>
                    <Col span={4}>
                      <Card>
                        <Statistic title="Forms" value={user.planUsage.forms} suffix={`/ ${user.plan.forms}`} />
                        <Typography.Text
                          className={styles.planUsageEdit}
                          editable={{ onChange: (value) => handlePlanUsageChange('forms', value) }}>
                          {user.planUsage.forms}
                        </Typography.Text>
                      </Card>
                    </Col>

                    <Col span={4}>
                      <Card>
                        <Statistic title="Submissions" value={user.planUsage.submissions} suffix={`/ ${user.plan.submissions}`} />
                        <Typography.Text
                          className={styles.planUsageEdit}
                          editable={{ onChange: (value) => handlePlanUsageChange('submissions', value) }}>
                          {user.planUsage.submissions}
                        </Typography.Text>
                      </Card>
                    </Col>

                    <Col span={4}>
                      <Card>
                        <Statistic title="Users" value={user.planUsage.users} suffix={`/ ${user.plan.users}`} />
                        <Typography.Text
                          className={styles.planUsageEdit}
                          editable={{ onChange: (value) => handlePlanUsageChange('users', value) }}>
                          {user.planUsage.users}
                        </Typography.Text>
                      </Card>
                    </Col>

                    <Col span={4}>
                      <Card>
                        <Statistic title="Guests" value={user.planUsage.guests} suffix={`/ ${user.plan.guests}`} />
                        <Typography.Text
                          className={styles.planUsageEdit}
                          editable={{ onChange: (value) => handlePlanUsageChange('guests', value) }}>
                          {user.planUsage.guests}
                        </Typography.Text>
                      </Card>
                    </Col>

                    <Col span={4}>
                      <Card>
                        <Statistic title="Storage" value={user.planUsage.filesStorage} suffix={`/ ${user.plan.filesStorage}`} />
                        <Typography.Text
                          className={styles.planUsageEdit}
                          editable={{ onChange: (value) => handlePlanUsageChange('filesStorage', value) }}>
                          {user.planUsage.filesStorage}
                        </Typography.Text>
                      </Card>
                    </Col>

                    <Col span={4}>
                      <Card>
                        <Statistic title="Domains" value={user.planUsage.domains} suffix={`/ ${user.plan.domains}`} />
                        <Typography.Text
                          className={styles.planUsageEdit}
                          editable={{ onChange: (value) => handlePlanUsageChange('domains', value) }}>
                          {user.planUsage.domains}
                        </Typography.Text>
                      </Card>
                    </Col>
                  </Row>
                </Tabs.TabPane>

                {user.authType !== 'onetool' && user.role === 'holder' && (
                  <Tabs.TabPane tab="Plan" key="plan">
                    <Alert message="This will not update Stripe, LTDF or planUsage properties!" type="warning" showIcon />

                    <Descriptions column={1} bordered style={{ margin: '20px 0 0 0' }}>
                      <Descriptions.Item label="forms" span={1}>
                        <Input.Group>
                          <Row gutter={8}>
                            <Col span={5}>
                              <Input
                                type="number"
                                value={planForms}
                                onChange={(e) => setPlanForms(parseInt(e.target.value, 10))}
                                onPressEnter={() => updatePlan('forms', planForms)}
                              />
                            </Col>
                            <Col span={8}>
                              <Button type="primary" ghost onClick={() => updatePlan('forms', planForms)}>
                                Update
                              </Button>
                              <Typography.Text style={{ margin: '0 0 0 20px' }} type="secondary">
                                from 1 to 1000
                              </Typography.Text>
                            </Col>
                          </Row>
                        </Input.Group>
                      </Descriptions.Item>

                      <Descriptions.Item label="submissions" span={1}>
                        <Input.Group>
                          <Row gutter={8}>
                            <Col span={5}>
                              <Input
                                type="number"
                                value={planSubmissions}
                                onChange={(e) => setPlanSubmissions(parseInt(e.target.value, 10))}
                                onPressEnter={() => updatePlan('submissions', planSubmissions)}
                              />
                            </Col>
                            <Col span={8}>
                              <Button type="primary" ghost onClick={() => updatePlan('submissions', planSubmissions)}>
                                Update
                              </Button>
                              <Typography.Text style={{ margin: '0 0 0 20px' }} type="secondary">
                                from 50 to 1000000
                              </Typography.Text>
                            </Col>
                          </Row>
                        </Input.Group>
                      </Descriptions.Item>

                      <Descriptions.Item label="users" span={1}>
                        <Input.Group>
                          <Row gutter={8}>
                            <Col span={5}>
                              <Input
                                type="number"
                                value={planUsers}
                                onChange={(e) => setPlanUsers(parseInt(e.target.value, 10))}
                                onPressEnter={() => updatePlan('users', planUsers)}
                              />
                            </Col>
                            <Col span={8}>
                              <Button type="primary" ghost onClick={() => updatePlan('users', planUsers)}>
                                Update
                              </Button>
                              <Typography.Text style={{ margin: '0 0 0 20px' }} type="secondary">
                                from 1 to 10000
                              </Typography.Text>
                            </Col>
                          </Row>
                        </Input.Group>
                      </Descriptions.Item>

                      <Descriptions.Item label="guests" span={1}>
                        <Input.Group>
                          <Row gutter={8}>
                            <Col span={5}>
                              <Input
                                type="number"
                                value={planGuests}
                                onChange={(e) => setPlanUsers(parseInt(e.target.value, 10))}
                                onPressEnter={() => updatePlan('guests', planGuests)}
                              />
                            </Col>
                            <Col span={8}>
                              <Button type="primary" ghost onClick={() => updatePlan('guests', planGuests)}>
                                Update
                              </Button>
                              <Typography.Text style={{ margin: '0 0 0 20px' }} type="secondary">
                                from 1 to 10000
                              </Typography.Text>
                            </Col>
                          </Row>
                        </Input.Group>
                      </Descriptions.Item>

                      <Descriptions.Item label="filesStorage" span={1}>
                        <Input.Group>
                          <Row gutter={8}>
                            <Col span={5}>
                              <Input
                                type="number"
                                value={planFilesStorage}
                                onChange={(e) => setPlanFilesStorage(parseInt(e.target.value, 10))}
                                onPressEnter={() => updatePlan('filesStorage', planFilesStorage)}
                              />
                            </Col>
                            <Col span={8}>
                              <Button type="primary" ghost onClick={() => updatePlan('filesStorage', planFilesStorage)}>
                                Update
                              </Button>
                              <Typography.Text style={{ margin: '0 0 0 20px' }} type="secondary">
                                from 50 to 10000
                              </Typography.Text>
                            </Col>
                          </Row>
                        </Input.Group>
                      </Descriptions.Item>

                      <Descriptions.Item label="domains" span={1}>
                        <Input.Group>
                          <Row gutter={8}>
                            <Col span={5}>
                              <Input
                                type="number"
                                value={planDomains}
                                onChange={(e) => setPlanDomains(parseInt(e.target.value, 10))}
                                onPressEnter={() => updatePlan('domains', planDomains)}
                              />
                            </Col>
                            <Col span={8}>
                              <Button type="primary" ghost onClick={() => updatePlan('domains', planDomains)}>
                                Update
                              </Button>
                              <Typography.Text style={{ margin: '0 0 0 20px' }} type="secondary">
                                from 0 to 1000
                              </Typography.Text>
                            </Col>
                          </Row>
                        </Input.Group>
                      </Descriptions.Item>

                      <Descriptions.Item label="brandingRemoval" span={1}>
                        <Input.Group>
                          <Row gutter={8}>
                            <Col span={5}>
                              <Input
                                type="number"
                                value={planBrandingRemoval}
                                onChange={(e) => setBrandingRemoval(parseInt(e.target.value, 10))}
                                onPressEnter={() => updatePlan('brandingRemoval', planBrandingRemoval)}
                              />
                            </Col>
                            <Col span={8}>
                              <Button type="primary" ghost onClick={() => updatePlan('brandingRemoval', planBrandingRemoval)}>
                                Update
                              </Button>
                              <Typography.Text style={{ margin: '0 0 0 20px' }} type="secondary">
                                0 or 1
                              </Typography.Text>
                            </Col>
                          </Row>
                        </Input.Group>
                      </Descriptions.Item>
                    </Descriptions>

                    <Row gutter={8} style={{ margin: '30px 0 0 0' }}>
                      <Col span={3}>Bulk update to</Col>
                      <Col span={12}>
                        <Button type="primary" style={{ margin: '0 10px 0 0' }} ghost onClick={() => bulkUpdatePlan('ltdSolo')}>
                          LTD solo
                        </Button>
                        <Button type="primary" style={{ margin: '0 10px 0 0' }} ghost onClick={() => bulkUpdatePlan('ltdAgency')}>
                          LTD agency
                        </Button>
                        <Button type="primary" style={{ margin: '0 10px 0 0' }} ghost onClick={() => bulkUpdatePlan('ltdBusiness')}>
                          LTD business
                        </Button>
                        <Button type="primary" style={{ margin: '0 10px 0 0' }} ghost onClick={() => bulkUpdatePlan('unlimited')}>
                          Unlimited
                        </Button>
                        <Button type="primary" style={{ margin: '0 10px 0 0' }} ghost onClick={() => bulkUpdatePlan('free')}>
                          Free
                        </Button>
                      </Col>
                    </Row>
                  </Tabs.TabPane>
                )}

                {user.authType !== 'onetool' && user.role === 'holder' && (
                  <Tabs.TabPane tab="LTDF" key="ltdf">
                    <Descriptions column={3} bordered style={{ margin: '20px 0 0 0' }}>
                      <Descriptions.Item label="Enabled">
                        <Switch
                          checked={user.lifeTime && user.lifeTime.enabled}
                          onChange={(value) => handleChange('lifeTime.enabled', value)}
                        />
                      </Descriptions.Item>

                      <Descriptions.Item label="Paid">
                        <Switch checked={user.lifeTime && user.lifeTime.paid} onChange={(value) => handleChange('lifeTime.paid', value)} />
                      </Descriptions.Item>
                      {/* 
                      <Descriptions.Item label="Manual (split payment, plan controller by root)" span={3}>
                        <Switch
                          checked={user.lifeTime && user.lifeTime.manual}
                          onChange={(value) => handleChange('lifeTime.manual', value)}
                        />
                      </Descriptions.Item> */}

                      <Descriptions.Item label="Plan" span={3}>
                        <Select value={user.lifeTime && user.lifeTime.plan} onChange={(value) => handleChange('lifeTime.plan', value)}>
                          <Select.Option value={null}>-- empty --</Select.Option>
                          <Select.Option value="solo">solo</Select.Option>
                          <Select.Option value="agency">agency</Select.Option>
                          <Select.Option value="business">business</Select.Option>
                        </Select>
                      </Descriptions.Item>

                      <Descriptions.Item label="Plan" span={3}>
                        <Select
                          value={user.lifeTime && user.lifeTime.users}
                          onChange={(value) => handleChange('lifeTime.users', parseInt(value))}>
                          <Select.Option value="0">0</Select.Option>
                          <Select.Option value="1">1</Select.Option>
                          <Select.Option value="2">2</Select.Option>
                          <Select.Option value="3">3</Select.Option>
                          <Select.Option value="4">4</Select.Option>
                          <Select.Option value="5">5</Select.Option>
                          <Select.Option value="6">6</Select.Option>
                          <Select.Option value="7">7</Select.Option>
                          <Select.Option value="8">8</Select.Option>
                          <Select.Option value="9">9</Select.Option>
                          <Select.Option value="10">10</Select.Option>
                        </Select>
                      </Descriptions.Item>
                    </Descriptions>

                    <Table
                      style={{ margin: '40px 0 0 0' }}
                      columns={ordersColumns}
                      dataSource={user.lifeTime ? user.lifeTime.orders : []}
                      title={() => (
                        <div className={styles.ordersLabel}>
                          <span>Orders</span>
                          <Button shape="circle" icon={<PlusOutlined />} onClick={() => setShowAddOrderModal(true)} />
                        </div>
                      )}
                    />

                    {user.lifeTime && user.lifeTime.split && user.lifeTime.split.enabled && (
                      <>
                        <Table
                          style={{ margin: '40px 0 0 0' }}
                          columns={splitColumns}
                          dataSource={[
                            { ...user.lifeTime.split.payment1, key: 1 },
                            { ...user.lifeTime.split.payment2, key: 2 },
                            { ...user.lifeTime.split.payment3, key: 3 }
                          ]}
                          title={() => 'Split payments'}
                        />
                      </>
                    )}
                  </Tabs.TabPane>
                )}

                {user.role === 'holder' && (
                  <Tabs.TabPane tab="Users" key="users">
                    <List
                      itemLayout="horizontal"
                      dataSource={user.users}
                      renderItem={(item) => (
                        <List.Item>
                          <List.Item.Meta title={<Link to={`/users/${item._id}`}>{item.email}</Link>} description={item.name} />
                          <div>{item.role}</div>
                        </List.Item>
                      )}
                    />
                  </Tabs.TabPane>
                )}

                {user.role === 'holder' && (
                  <Tabs.TabPane tab="Forms" key="forms">
                    <List
                      itemLayout="horizontal"
                      dataSource={user.forms}
                      renderItem={(item) => {
                        return (
                          <List.Item>
                            <List.Item.Meta title={<Link to={`/forms/${item._id}`}>{item.name}</Link>} />
                            <div>
                              {item.deleted && <Tag color="red">deleted</Tag>}
                              {item.type === 'classic' && <Tag color="blue">classic</Tag>}
                              {item.type === 'conversational' && <Tag color="gold">conversational</Tag>}
                              {item.status === 'live' && <Tag color="green">live</Tag>}
                              {item.status === 'paused' && <Tag color="orange">paused</Tag>}
                            </div>
                          </List.Item>
                        );
                      }}
                    />
                  </Tabs.TabPane>
                )}

                {/* {user.authType !== 'onetool' && (
                  <Tabs.TabPane tab="Stripe" key="stripe">
                    <Descriptions column={3} bordered style={{ margin: '20px 0 0 0' }}>
                      <Descriptions.Item label="Current Plan Period" span={3}>
                        {moment(user.stripe.planPeriod.start).format('DD.MM.YYYY HH:mm')} -{' '}
                        {moment(user.stripe.planPeriod.end).format('DD.MM.YYYY HH:mm')}
                        <Statistic.Countdown title="" value={moment(user.stripe.planPeriod.end)} format="Dd Hh m:s" />
                      </Descriptions.Item>

                      <Descriptions.Item label="Customer ID">{user.stripe.customerId}</Descriptions.Item>

                      <Descriptions.Item label="Subscription ID">{user.stripe.subscriptionId}</Descriptions.Item>

                      <Descriptions.Item label="Collection Method">{user.stripe.collectionMethod}</Descriptions.Item>

                      {user.stripe.customer && (
                        <Descriptions.Item label="Account Balance">{user.stripe.customer.account_balance}</Descriptions.Item>
                      )}

                      {user.stripe.customer && (
                        <Descriptions.Item label="Discount">
                          {user.stripe.customer.discount ? (
                            <>
                              {user.stripe.customer.discount.coupon.name} ({user.stripe.customer.discount.coupon.percent_off}% off{' '}
                              {user.stripe.customer.discount.coupon.duration})
                            </>
                          ) : (
                            'none'
                          )}
                        </Descriptions.Item>
                      )}
                    </Descriptions>

                    <List
                      header="Invoices"
                      bordered
                      itemLayout="horizontal"
                      style={{ margin: '30px 0 0 0' }}
                      dataSource={user.stripe.invoices}
                      renderItem={(item) => (
                        <List.Item>
                          <List.Item.Meta
                            title={
                              <a href={`https://dashboard.stripe.com/invoices/${item.id}`} target="_blank">
                                {item.number}
                              </a>
                            }
                            description={`Created ${moment(item.created * 1000).format('DD.MM.YYYY HH:mm')}`}
                          />
                          <div>
                            <span style={{ margin: '0 20px 0 0' }}>{item.amount_due / 100}€</span>
                            {!item.paid && moment().diff(item.due_date * 1000, 'days') > 0 && <Tag color="red">expired</Tag>}
                            {item.paid && <Tag color="green">paid</Tag>}
                            {!item.paid && <Tag color="orange">unpaid</Tag>}
                            <Tag>{item.collection_method === 'charge_automatically' ? 'automatically' : 'manual'}</Tag>
                          </div>
                        </List.Item>
                      )}
                    />
                  </Tabs.TabPane>
                )} */}
                {/* 
                {user.authType !== 'onetool' && user.role === 'holder' && (
                  <Tabs.TabPane tab="Mollie" key="mollie">
                    <MollieOrder />
                  </Tabs.TabPane>
                )} */}

                {user.authType !== 'onetool' && (
                  <Tabs.TabPane tab="Domains" key="domains">
                    <List
                      itemLayout="horizontal"
                      dataSource={user.domains}
                      renderItem={(item) => {
                        return (
                          <List.Item>
                            <List.Item.Meta title={item.hostname} />
                          </List.Item>
                        );
                      }}
                    />
                  </Tabs.TabPane>
                )}

                <Tabs.TabPane tab="Played Tutorials" key="playedTutorials">
                  <List
                    itemLayout="horizontal"
                    dataSource={_uniq(user.playedTutorials || [])}
                    renderItem={(item) => {
                      return (
                        <List.Item>
                          <List.Item.Meta title={item.key} description={<>{round(item.completionRatio * 100)}%</>} />
                        </List.Item>
                      );
                    }}
                  />
                </Tabs.TabPane>

                {user.authType === 'onetool' && (
                  <Tabs.TabPane tab="Onetool" key="onetool">
                    <Descriptions column={2} bordered style={{ margin: '20px 0 0 0' }}>
                      <Descriptions.Item label="First Name">{user.onetool.firstName}</Descriptions.Item>

                      <Descriptions.Item label="Last Name">{user.onetool.lastName}</Descriptions.Item>

                      <Descriptions.Item label="Name">{user.onetool.name}</Descriptions.Item>

                      <Descriptions.Item label="Street">{user.onetool.street}</Descriptions.Item>

                      <Descriptions.Item label="City">{user.onetool.city}</Descriptions.Item>

                      <Descriptions.Item label="Zip">{user.onetool.zip}</Descriptions.Item>

                      <Descriptions.Item label="Country">{user.onetool.country}</Descriptions.Item>

                      <Descriptions.Item label="Trial">
                        <Switch checked={user.onetool.inTrial} disabled onChange={() => {}} />
                      </Descriptions.Item>

                      <Descriptions.Item label="Plan ID">{user.onetool.planId}</Descriptions.Item>
                    </Descriptions>
                  </Tabs.TabPane>
                )}
              </Tabs>
            </>
          )}
        </Card>
      </Spin>

      <Modal title="Create order" visible={showAddOrderModal} onOk={() => {}} onCancel={() => setShowAddOrderModal(false)}>
        <Form name="order" onFinish={(values) => createOrder(values)}>
          <Form.Item name={['order', 'id']} label="ID">
            <Input />
          </Form.Item>

          <Form.Item name={['order', 'amount']} label="amount">
            <Input />
          </Form.Item>

          <Form.Item name={['order', 'vatAmount']} label="vatAmount">
            <Input />
          </Form.Item>

          <Form.Item name={['order', 'vatRate']} label="vatRate">
            <Input />
          </Form.Item>

          <Form.Item name={['order', 'description']} label="description">
            <Input />
          </Form.Item>

          <Form.Item name={['order', 'createdAt']} label="createdAt">
            <DatePicker />
          </Form.Item>

          <Button type="primary" htmlType="submit">
            Save
          </Button>
        </Form>
      </Modal>
    </>
  );
};

export default withRouter(User);
